import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Box, Button, Tooltip } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

import Header from "./Header";
import PartA from "./PartA";
import PartB from "./PartB";
import Footer from "./Footer";
import "./template.css";
import { instructionsToText } from "../create/paperBuilder";
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";

// Blank header scaffold — the template renders ONLY the real paper data passed in.
// No sample/placeholder content is ever injected; missing fields just stay empty.
const BLANK_DEFAULTS = {
  university: "",
  examTitle: "",
  collegeName: "",
  department: "",
  subjectCode: "",
  subjectName: "",
  semester: "",
  regulation: "",
  academicYear: "",
  time: "",
  maxMarks: "",
  footerNote: "",
  instructions: [],
};

const mergeData = (overrides) => {
  const merged = { ...BLANK_DEFAULTS };
  if (overrides) {
    Object.keys(overrides).forEach((key) => {
      const val = overrides[key];
      if (val === undefined) return;
      merged[key] = val === null ? "" : val;
    });
  }
  return merged;
};

// The A4 sheet itself — reused for both the on-screen preview and the print portal.
const PaperSheet = ({ paper, editable }) => (
  <div className="qp-page" contentEditable={editable} suppressContentEditableWarning>
    <div className="qp-watermark">TALENT IQ</div>
    <div className="qp-content">
      <Header data={paper} />
      {instructionsToText(paper.instructions).trim() && (
        <div className="qp-paper-instructions">
          <strong>General Instruction(s):</strong>
          {"\n"}
          {instructionsToText(paper.instructions)}
        </div>
      )}
      {Array.isArray(paper.parts) && paper.parts.length > 0 ? (
        paper.parts.map((p, i) => (p.pattern === "list" ? <PartA key={i} part={p} /> : <PartB key={i} part={p} />))
      ) : (
        <div style={{ textAlign: "center", color: "#8e9ebc", fontStyle: "italic", padding: "40px 0" }}>
          No questions added yet.
        </div>
      )}
      <Footer note={paper.footerNote} />
    </div>
  </div>
);

const QuestionPaperTemplate = ({ data }) => {
  const paper = mergeData(data);
  const [dark, setDark] = useState(false);

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // Both actions use the browser print dialog; "Save as PDF" in the dialog
  // produces the PDF. The portal copy (see below) is the only thing that prints.
  const handlePrint = () => {
    document.title = paper.subjectName || paper.examTitle || `${getLabel('QUESTION_PAPER')}`;
    window.print();
  };

  return (
    <div className={`qp-wrapper${dark ? " qp-dark" : ""}`}>
      <Box className="qp-no-print qp-toolbar">
        <Button
          variant="outlined"
          size="small"
          startIcon={dark ? <LightModeIcon /> : <DarkModeIcon />}
          onClick={() => setDark((p) => !p)}
          sx={{ textTransform: "none", borderRadius: "10px" }}
        >
          {dark ? "Light" : "Dark"}
        </Button>
        {/* <Tooltip title="Opens the print dialog — choose your printer" arrow>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            Print
          </Button>
        </Tooltip> */}
        <Tooltip title="Opens the print dialog — choose 'Save as PDF'" arrow>
          <Button
            variant="contained"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePrint}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            Export to PDF
          </Button>
        </Tooltip>
      </Box>

      {/* On-screen preview (hidden while printing) */}
      <div className="qp-preview qp-no-print">
        <PaperSheet paper={paper} editable={false} />
      </div>

      {/* Print-only copy mounted at <body> level so the rest of the app contributes
          no page height — prevents the stray blank/second page when printing. */}
      {ReactDOM.createPortal(
        <div className="qp-print-mount">
          <PaperSheet paper={paper} editable={false} />
        </div>,
        document.body
      )}
    </div>
  );
};

export default QuestionPaperTemplate;
